import type { MissiveLeadActivityResult } from 'lib/missive/lead-activity';
import { useEffect, useState } from 'react';

import { useSession } from 'providers/session';

type UseLeadMissiveActivityOptions = {
  email?: string | null;
  phone?: string | null;
  enabled?: boolean;
};

export default function useLeadMissiveActivity({
  email,
  phone,
  enabled = true,
}: UseLeadMissiveActivityOptions) {
  const { token } = useSession();
  const [data, setData] = useState<MissiveLeadActivityResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !token || (!email?.trim() && !phone?.trim())) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();

        if (email?.trim()) {
          params.set('email', email.trim());
        }

        if (phone?.trim()) {
          params.set('phone', phone.trim());
        }

        const response = await fetch(
          `/api/missive/lead-activity?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload?.error?.message || 'Failed to load Missive activity.',
          );
        }

        if (!cancelled) {
          setData(payload);
        }
      } catch (fetchError: any) {
        if (!cancelled) {
          setData(null);
          setError(fetchError?.message || 'Failed to load Missive activity.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [email, phone, enabled, token]);

  return { data, loading, error };
}
